"use client"

import { PanelRight, ArrowLeft } from "lucide-react"
import { cn } from "@/lib/utils"

interface CollapsedRightRailProps {
  onOpen: () => void
  badgeCount?: number
  ariaLabel?: string
}

/**
 * Shared collapsed right-rail component with unified hover behaviors:
 * - Surface darken on hover
 * - PanelRight → ArrowLeft icon morph on hover
 * - Top-aligned icon (matches left sidebar rhythm with h-16 header)
 * - Badge centered beneath icon (not floating top-right)
 */
export function CollapsedRightRail({ onOpen, badgeCount, ariaLabel = "Open panel" }: CollapsedRightRailProps) {
  return (
    <div
      className={cn(
        "hidden lg:flex flex-col items-center",
        "w-16 h-screen sticky top-0",
        "bg-[var(--surface-context)]",
        "shadow-[inset_4px_0_8px_rgba(0,0,0,0.07)]",
        "cursor-pointer",
        "transition-colors duration-150",
        "hover:brightness-[0.97]",
        "group",
      )}
      style={{ flexShrink: 0 }}
      onClick={onOpen}
      role="button"
      aria-label={ariaLabel}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          onOpen()
        }
      }}
    >
      <div className="h-16 flex items-center justify-center shrink-0">
        <div className="relative h-9 w-9 flex items-center justify-center">
          <PanelRight
            className={cn(
              "h-4 w-4 absolute",
              "transition-opacity duration-150",
              "opacity-100 group-hover:opacity-0",
              "text-[var(--text-secondary)]",
            )}
          />
          <ArrowLeft
            className={cn(
              "h-4 w-4 absolute",
              "transition-opacity duration-150",
              "opacity-0 group-hover:opacity-100",
              "text-[var(--text-secondary)]",
            )}
          />
        </div>
      </div>

      {badgeCount !== undefined && badgeCount > 0 && (
        <div className="mt-2 flex items-center justify-center">
          <div className="h-6 w-6 rounded-full bg-blue-600 text-white text-xs font-semibold flex items-center justify-center">
            {badgeCount}
          </div>
        </div>
      )}

      {/* Spacer to keep rest empty */}
      <div className="flex-1" />
    </div>
  )
}
